import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ConfigProvider from '../config-provider';
import Message from '../message';
import Button from '../button';
import zhCN from '../locale/zh-cn';
import dialog from './dialog';

const Dialog = ConfigProvider.config(dialog);

const noop = () => {};

export const ModalInner = function({
  type,
  messageProps = {},
  title,
  rtl,
  prefix = 'next-',
  content,
}) {
  return (
    <Message
      {...messageProps}
      closeable={false}
      shape="addon"
      size="large"
      type={type}
      title={title}
      rtl={rtl}
      className={cx(`${prefix}dialog-message`, messageProps.className)}
    >
      {content}
    </Message>
  );
};

class Modal extends Component {
  static propTypes = {
    prefix: PropTypes.string,
    pure: PropTypes.bool,
    rtl: PropTypes.bool,
    type: PropTypes.string,
    title: PropTypes.node,
    content: PropTypes.node,
    messageProps: PropTypes.object,
    footerActions: PropTypes.array,
    /**
     * Callback function triggered when Ok button is clicked
     * @param {Object} event click event object
     * @returns {Promise} Optionally handles a Promise return object
     */
    onOk: PropTypes.func,
    /**
     * Callback function triggered when Cancel button is clicked
     * @param {Object} event click event object
     * @returns {Promise} Optionally handles a Promise return object
     */
    onCancel: PropTypes.func,
    /**
     * Callback function triggered when Close button is clicked
     * @param {Object} event click event object
     * @returns {Promise} Optionally handles a Promise return object
     */
    onClose: PropTypes.func,
    okProps: PropTypes.object,
    locale: PropTypes.object,
    className: PropTypes.string,
  };

  static defaultProps = {
    prefix: 'next-',
    type: 'notice',
    pure: false,
    messageProps: {},
    onOk: noop,
    onCancel: noop,
    onClose: noop,
    okProps: {},
    locale: zhCN.Dialog,
  };

  state = {
    visible: true,
    loading: false,
  };

  close = () => {
    this.setState({
      visible: false,
    });
  };

  loading = loading => {
    this.setState({
      loading,
    });
  };

  wrapper(fn, callback) {
    return () => {
      const res = fn();
      if (res && res.then) {
        this.loading(true);

        res
          .then(result => {
            this.loading(false);

            if (result !== false) {
              return callback();
            }
          })
          .catch(e => {
            this.loading(false);
            throw e;
          });
      } else if (res !== false) {
        return callback();
      }
    };
  }

  render() {
    const {
      prefix,
      type,
      title,
      content,
      messageProps,
      footerActions,
      onOk,
      onCancel,
      onClose,
      okProps,
      rtl,
      className,
      ...others
    } = this.props;

    const newFooterActions = footerActions || ['cancel', 'ok'];
    const newOnOk = this.wrapper(onOk, this.close);
    const newOnCancel = this.wrapper(onCancel, this.close);
    const newOnClose = this.wrapper(onClose, this.close);
    const newTitle = (
      <div className={`${prefix}dialog-quick-header`}>
        <span className={`${prefix}dialog-quick-title`}>{title}</span>
        <Button.Icon type="primary" name="remove" onClick={newOnClose} color="gray" />
      </div>
    );
    const newContent = type ? (
      <ModalInner
        messageProps={messageProps}
        type={type}
        title={newTitle}
        rtl={rtl}
        prefix={prefix}
        content={content}
        closeable={false}
      />
    ) : (
      content
    );

    const { visible, loading } = this.state;
    okProps.loading = loading;

    const classNames = cx(`${prefix}dialog-quick`, className);

    return (
      <Dialog
        role="alertdialog"
        {...others}
        visible={visible}
        rtl={rtl}
        footerActions={newFooterActions}
        onOk={this.state.loading ? noop : newOnOk}
        onCancel={newOnCancel}
        onClose={newOnClose}
        okProps={okProps}
        className={classNames}
      >
        {newContent}
      </Dialog>
    );
  }
}

const ConfigModal = ConfigProvider.config(Modal, { componentName: 'Dialog' });

/**
 * 创建对话框
 * @exportName show
 * @param {Object} config 配置项
 * @returns {Object} 包含有 hide 方法，可用来关闭对话框
 */
export const show = (config = {}) => {
  const container = document.createElement('div');
  let currentConfig = { ...config };

  const unmount = () => {
    if (currentConfig.afterClose) {
      currentConfig.afterClose();
    }
    ReactDOM.unmountComponentAtNode(container);
    container.parentNode.removeChild(container);
  };

  document.body.appendChild(container);
  const newContext = ConfigProvider.getContext();

  let instance;

  const render = (modalConfig = {}) => {
    setTimeout(() => {
      ReactDOM.render(
        <ConfigProvider {...newContext}>
          <ConfigModal
            {...modalConfig}
            afterClose={unmount}
            ref={ref => {
              instance = ref;
            }}
          />
        </ConfigProvider>,
        container,
      );
    });
  };

  render(currentConfig);

  return {
    hide: () => {
      const inc = instance && instance.getInstance();
      inc && inc.close();
    },
    update: (newConfig = {}) => {
      currentConfig = {
        ...currentConfig,
        ...newConfig,
      };
      render(currentConfig);
    },
  };
};

const methodFactory = type => (config = {}) => {
  config.type = type;
  return show(config);
};

/**
 * 创建提示对话框
 * @exportName notice
 * @param {Object} config 配置项
 * @returns {Object} 包含有 hide 方法，可用来关闭对话框
 */
export const notice = methodFactory('notice');

/**
 * 创建警告对话框
 * @exportName warning
 * @param {Object} config 配置项
 * @returns {Object} 包含有 hide 方法，可用来关闭对话框
 */
export const warning = methodFactory('warning');

/**
 * 创建错误对话框
 * @exportName error
 * @param {Object} config 配置项
 * @returns {Object} 包含有 hide 方法，可用来关闭对话框
 */
export const error = methodFactory('error');

/**
 * 创建成功对话框
 * @exportName success
 * @param {Object} config 配置项
 * @returns {Object} 包含有 hide 方法，可用来关闭对话框
 */
export const success = methodFactory('success');
